import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

export default function MealScheduleCard({ business }) {
  const [mealPlans, setMealPlans] = useState([]);

  // Hostel category IDs
  const HOSTEL_CATEGORY_IDS = [
    'a5363951-3c77-473b-be80-5755f363ce2a', // Boys Hostel
    'b6775f00-532c-41b7-8bc4-c389c1832cce'  // Girls Hostel
  ];

  // Only show for hostel categories - check by category ID
  const isHostel = 
    HOSTEL_CATEGORY_IDS.includes(business?.category?.id) ||
    business?.categories?.some(c => HOSTEL_CATEGORY_IDS.includes(c?.id));

  useEffect(() => {
    // Only fetch if it's a hostel
    if (!isHostel || !business?.id) return;
    
    const fetchMealPlans = async () => {
      try {
        const response = await fetch(
          `https://api.kaha.com.np/hostel/api/v1/meal-plans/public?businessId=${business.id}`
        );
        if (response.ok) {
          const data = await response.json();
          setMealPlans(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching meal plans:', error);
      }
    };
    fetchMealPlans();
  }, [business?.id, isHostel]);

  // Early return if not a hostel
  if (!isHostel) return null;

  if (!mealPlans || mealPlans.length === 0) return null;

  const daysOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const sortedMealPlans = daysOrder.map(day => mealPlans.find(plan => plan.day === day)).filter(Boolean);

  // Get current day
  const currentDay = daysOrder[new Date().getDay()];

  // Function to get abbreviated day name
  const getAbbreviatedDay = (day) => {
    const abbreviations = {
      'Sunday': 'Sun',
      'Monday': 'Mon',
      'Tuesday': 'Tue',
      'Wednesday': 'Wed',
      'Thursday': 'Thu',
      'Friday': 'Fri',
      'Saturday': 'Sat'
    };
    return abbreviations[day] || day;
  };

  // Only render if we have meal plans and it's a hostel
  if (!sortedMealPlans || sortedMealPlans.length === 0) return null;

  return (
    <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-4 sm:p-6 backdrop-blur-xl border border-white/20 hover:border-green-400/50 transition-all duration-500">
      {/* Subtle inner glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-transparent pointer-events-none rounded-2xl"></div>
      
      <div className="relative z-10">
        <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-green-400" />
          Meal Schedule
        </h2>
        
        {/* Header */}
        <div className="grid gap-0 mb-2 text-[10px] sm:text-sm border-b border-white/30 bg-white/5" style={{ gridTemplateColumns: 'minmax(40px, 0.6fr) repeat(4, 1fr)' }}>
          <div className="text-white font-bold px-1 sm:px-3 py-3 border-r border-white/20 flex items-center justify-center">Day</div>
          <div className="text-white font-bold px-1 sm:px-3 py-3 border-r border-white/20 flex items-center justify-center text-center leading-tight">
            Breakfast
          </div>
          <div className="text-white font-bold px-1 sm:px-3 py-3 border-r border-white/20 flex items-center justify-center">Lunch</div>
          <div className="text-white font-bold px-1 sm:px-3 py-3 border-r border-white/20 flex items-center justify-center">Snacks</div>
          <div className="text-white font-bold px-1 sm:px-3 py-3 flex items-center justify-center">Dinner</div>
        </div>
        
        {/* Rows */}
        <div className="space-y-1.5">
          {sortedMealPlans.map((plan, index) => {
            const isCurrentDay = plan.day === currentDay;
            
            return (
              <div 
                key={plan.day} 
                className={`grid gap-0 py-2 sm:py-2.5 rounded-lg transition-all text-[10px] sm:text-sm ${
                  isCurrentDay 
                    ? 'bg-yellow-500/20 border border-yellow-400/50' 
                    : `${index % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-900/20'} border border-transparent hover:bg-gray-700/40`
                }`}
                style={{ gridTemplateColumns: 'minmax(40px, 0.6fr) repeat(4, 1fr)' }}
              >
                <div className={`font-semibold capitalize px-1 sm:px-3 border-r flex items-center justify-center text-center text-[10px] sm:text-sm break-words ${isCurrentDay ? 'text-yellow-300 border-yellow-400/30' : 'text-gray-200 border-gray-600/30'}`}>
                  <span className="sm:hidden">{getAbbreviatedDay(plan.day)}</span>
                  <span className="hidden sm:inline">{plan.day}</span>
                </div>
                <div className={`px-1 sm:px-3 border-r flex items-center justify-center text-center text-[10px] sm:text-sm break-words ${isCurrentDay ? 'text-yellow-300 border-yellow-400/30' : 'text-gray-300 border-gray-600/30'}`}>
                  {plan.breakfast?.item || '-'}
                </div>
                <div className={`px-1 sm:px-3 border-r flex items-center justify-center text-center text-[10px] sm:text-sm break-words ${isCurrentDay ? 'text-yellow-300 border-yellow-400/30' : 'text-gray-300 border-gray-600/30'}`}>
                  {plan.lunch?.item || '-'}
                </div>
                <div className={`px-1 sm:px-3 border-r flex items-center justify-center text-center text-[10px] sm:text-sm break-words ${isCurrentDay ? 'text-yellow-300 border-yellow-400/30' : 'text-gray-300 border-gray-600/30'}`}>
                  {plan.snacks?.item || '-'}
                </div>
                <div className={`px-1 sm:px-3 flex items-center justify-center text-center text-[10px] sm:text-sm break-words ${isCurrentDay ? 'text-yellow-300' : 'text-gray-300'}`}>
                  {plan.dinner?.item || '-'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
